/**
 * widePolylineBand — the wide-polyline BAND as a filled plot path, shared by
 * every converter that can meet a polyline carrying DXF width (groups 40 / 41
 * per vertex, 43 entity-level on an LWPOLYLINE, 40 / 41 entity-level on a
 * heavy POLYLINE): the top-level `LWPOLYLINE` and `POLYLINE` converters and
 * both of `insertConverter.ts`'s block-child arms.
 *
 * # Why this exists
 *
 * Until it did, the plot stroked the CENTRELINE of a wide polyline at the
 * resolved lineweight while `batchedLayer.ts` drew the real band on screen —
 * on `abi1-test.dxf`, 23 entities up to 80 world units across, plotted as a
 * hairline. That divergence was introduced by the render-side band, so it is
 * closed here rather than recorded as a gap.
 *
 * # ONE band geometry, not a second offset implementation
 *
 * The offset edges, the mitred joints and the square caps are
 * `scene3d/adapters/geom/polylineWidthBand.ts`'s
 * {@link widePolylineBandLoops} — the SAME call the canvas makes; the
 * bulged-span subdivision and the taper that feed it are the SAME span walk
 * `batchedLayer.ts` tessellates the on-screen centreline with
 * (`.../geom/polylineWidthBand.ts::polylineSpanSubSegments`); and the width
 * each span is drawn at is `drawing-model`'s `polylineWidth.ts`, the one place
 * the DXF group 40 / 41 / 43 rule is written. Nothing about where the band goes is
 * decided in this module: it maps the resulting loops through the plot affine
 * and wraps them as a {@link PathPrimitive}. A bulged span therefore plots as
 * the band FOLLOWING ITS ARC (subdivided at the editor's chord tolerance),
 * never flattened to the chord, and a taper across that arc is carried onto
 * every sub-band.
 *
 * # FILLMODE — read from the DOCUMENT, not assumed
 *
 * `FILLMODE` "specifies whether hatches and fills, 2D solids, and wide
 * polylines are filled in": 1 (the documented INITIAL VALUE, saved in the
 * drawing) fills them, 0 draws them as OUTLINES only. `ezdxf` carries the same
 * var at the same group code with the same default (`sections/headervars.py`:
 * `$FILLMODE`, code 70, `default=1`, DXF12-2018), which is why an ABSENT value
 * reads as 1 here and never as 0.
 *
 * So a band has exactly TWO plotted appearances and the drawing picks between
 * them ({@link bandPathPrimitive}):
 *
 *   FILLMODE 1 — a FILL-ONLY {@link PathPrimitive} (`fill: "nonzero"`,
 *     `stroke: false`). Stroking the silhouette as well would inflate the
 *     plotted band by half a lineweight on each edge, which is not what
 *     AutoCAD puts on paper: at FILLMODE 1 the band's boundary IS the edge of
 *     the filled region. `ezdxf`'s own renderer agrees verbatim —
 *     `addons/drawing/frontend.py`, on `entity.has_width`, builds a
 *     `TraceBuilder`, calls `draw_filled_polygon` with a SOLID `Filling()` for
 *     each band polygon and RETURNS; the centreline path is never drawn and the
 *     band is never stroked. (It emits one filled quad per station pair where
 *     we emit the joined silhouette; `LinearTrace` places its shared corners by
 *     intersecting the adjacent offset edges, which is the same mitre, so the
 *     union of its quads IS that silhouette.) It also builds the trace in the
 *     entity's OCS and lifts the finished polygons to WCS, which is why the
 *     band here is offset in the polyline's own frame and only then mapped
 *     through `m`.
 *
 *   FILLMODE 0 — a STROKE-ONLY primitive over the SAME rings (`fill: "none"`).
 *     Every ring is stroked, the inner edge of a closed run's annulus
 *     included, because at FILLMODE 0 the whole silhouette is the ink.
 *
 * The GEOMETRY is identical in both states: the variable decides the paint, not
 * the shape, so the extents, the taper, the joints, the caps and the arc a
 * bulged span follows are the same either way.
 *
 * THE VALUE ARRIVES ON THE CONTEXT. `CadConvertContext.fillmode` is
 * `DrawingDocument.fillmode`, threaded by `sheetRenderer.ts`; the predicate
 * that reads it is `scene3d/adapters/geom/polylineDrawnBand.ts`'s
 * {@link fillModePaintsSolid} — the SAME call the canvas's fill mesh
 * (`scene/widePolylineFillMesh.ts`), its relift fan-out and its pointer pick
 * make. One truth, so screen and paper cannot hold two opinions about the
 * variable. Before @codex PR #785 round 12 F1 this module had no channel for
 * it and filled unconditionally, which round 11 turned from a latent
 * disagreement into a live one: the canvas learned to outline at 0 while the
 * plot went on painting a solid ribbon.
 *
 * WHAT THE CANVAS DOES DIFFERENTLY, and why it is not a second opinion: the
 * canvas strokes the silhouette in BOTH states and ADDS the interior mesh at 1
 * (`16536d1`), because its stroke carries two things a plot does not need — the
 * per-instance selection byte (a LINE-bucket slot) and a minimum on-screen
 * width (triangles have no rasterisation floor). Paper has neither concern, so
 * the plot paints exactly one of the two ops.
 *
 * A residual in both states: a wide polyline carrying a DASHED linetype plots
 * as a continuous band, because a fill has no dash pattern. AutoCAD breaks the
 * band itself along the pattern. Closing that means clipping the band to the
 * dash run, which is a linetype problem rather than a band problem, and the
 * same reading is what `ezdxf` produces.
 *
 * # Fill rule at FILLMODE 1: NONZERO, with the inner ring reversed
 *
 * A CLOSED run's band is an ANNULUS — {@link widePolylineBandLoops} returns
 * the outer and inner edge as two separate rings, both traversed in the
 * polyline's own direction and therefore both wound the SAME way. Reversing
 * every ring after the first makes their windings opposite, so a NONZERO fill
 * cancels to 0 inside the inner ring and paints the annulus instead of a solid
 * blob.
 *
 * Nonzero, not even-odd, is the correct rule here for two further reasons:
 * a band that overlaps ITSELF (a polyline doubling back, or a joint whose
 * inner offset crosses) is SOLID in AutoCAD, which nonzero reproduces and
 * even-odd would punch holes in; and `batchPaint.ts` COALESCES a whole style
 * run into one paint op, so under even-odd two overlapping bands of the same
 * style would erase each other's overlap.
 */

import {
  hasPolylineWidth,
  heavyPolylineWidths,
  lwpolylineWidths,
  type LwpolylineEntity,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";
import type { HeavyPolylineRun } from "@dxf-ai-editor/cad-core";

import type { Vec3 } from "../../../scene3d/types";
import { widePolylineBandLoops } from "../../../scene3d/adapters/geom/polylineWidthBand";
import { fillModePaintsSolid } from "../../../scene3d/adapters/geom/polylineDrawnBand";

import type {
  CadConvertContext,
  CadPrimitive,
  PathPrimitive,
  PathSegment,
  PathSubpath,
  PlotStyle,
  Point2,
} from "../cadPrimitive";
import { apply, type Affine2D } from "./affine2d";
import { resolvePlotStyle, type StyledEntity } from "./resolveStyle";

/**
 * A wide polyline's band as plot geometry: the first closed ring inline (the
 * shape {@link PathPrimitive} carries) plus any further rings as `subpaths`,
 * already reversed for the nonzero rule.
 */
export interface WidePolylineBand {
  start: Point2;
  segments: PathSegment[];
  subpaths?: PathSubpath[];
}

/** One outline ring → a closed plot subpath, mapped through `m`. */
function ringToSubpath(ring: readonly Vec3[], m: Affine2D): PathSubpath | null {
  // Fewer than three corners bounds no area, so it would fill nothing.
  if (ring.length < 3) return null;
  const points = ring.map((p) => apply(m, p));
  const head = points[0];
  if (!head) return null;
  return {
    start: head,
    segments: points.slice(1).map((to) => ({ kind: "line" as const, to })),
  };
}

/** The same ring traversed backwards (flips its winding). */
function reverseSubpath(sub: PathSubpath): PathSubpath {
  const points: Point2[] = [sub.start];
  for (const seg of sub.segments) points.push(seg.to);
  points.reverse();
  return {
    start: points[0]!,
    segments: points.slice(1).map((to) => ({ kind: "line" as const, to })),
  };
}

/**
 * Outline rings (band-local) → the plot band under `m`, or `null` when the
 * rings bound nothing. Every ring after the first is REVERSED so a nonzero
 * fill cuts the closed run's annulus (see the module doc).
 */
export function bandFromLoops(
  loops: readonly (readonly Vec3[])[],
  m: Affine2D,
): WidePolylineBand | null {
  const rings: PathSubpath[] = [];
  for (const loop of loops) {
    const sub = ringToSubpath(loop, m);
    if (sub) rings.push(sub);
  }
  const outer = rings[0];
  if (!outer) return null;
  const inner = rings.slice(1).map(reverseSubpath);
  return {
    start: outer.start,
    segments: outer.segments,
    ...(inner.length > 0 ? { subpaths: inner } : {}),
  };
}

/**
 * An `LWPOLYLINE`'s band under `m`, or `null` when it declares no width (the
 * caller then plots the centreline exactly as before).
 */
export function lwpolylineBand(
  entity: LwpolylineEntity,
  m: Affine2D,
): WidePolylineBand | null {
  const verts = entity.vertices;
  if (verts.length < 2) return null;
  const widths = lwpolylineWidths(entity);
  if (!hasPolylineWidth(widths)) return null;
  const points: Vec3[] = verts.map((v) => ({ x: v.x, y: v.y, z: 0 }));
  const bulges = verts.map((v) => v.bulge ?? 0);
  // No `extrusion` argument: these points never left the entity's own plane,
  // so the band is already built IN it and `m` carries the lift afterwards.
  const loops = widePolylineBandLoops(points, entity.closed, bulges, widths);
  return bandFromLoops(loops, m);
}

/**
 * A heavy `POLYLINE` vertex run's band under `m`, or `null` when the run
 * declares no width. `indices` are the run's VISIBLE records (already filtered
 * by the caller), and the points are the records' OCS/WCS XY — `m` carries the
 * OCS lift and any block affine, and the band is built BEFORE it, in the
 * entity's own plane, which is the only order that is right: the lift into a
 * TILTED plane is not width-preserving once the result is projected onto the
 * page, so offsetting after it would draw a band the file does not describe
 * (the defect the canvas had until @codex round 5 FINDING B —
 * `scene3d/adapters/geom/polylinePlane.ts`).
 *
 * Mesh subtypes never reach here: their wire runs are a wireframe, not a
 * width-carrying path, which is also why the renderer builds them as
 * `mesh-wire` geometry with no `widths` field.
 */
export function heavyPolylineBand(
  entity: PolylineEntity,
  run: HeavyPolylineRun,
  indices: readonly number[],
  m: Affine2D,
): WidePolylineBand | null {
  if (indices.length < 2) return null;
  const widths = heavyPolylineWidths(entity, indices);
  if (!hasPolylineWidth(widths)) return null;
  const points: Vec3[] = indices.map((i) => {
    const v = entity.vertices[i]!;
    return { x: v.location.x, y: v.location.y, z: 0 };
  });
  const loops = widePolylineBandLoops(points, run.closed, run.bulges, widths);
  return bandFromLoops(loops, m);
}

/**
 * Wrap a band as the {@link PathPrimitive} the drawing's `$FILLMODE` calls for:
 * FILL-ONLY at 1 (and at an absent value — the documented initial state), and
 * STROKE-ONLY over the same rings at 0. See the module doc's FILLMODE section.
 *
 * `fillmode` is a REQUIRED parameter of an optional type, deliberately: making
 * it optional would let a future call site fall silently back to "fill", which
 * is precisely the state this closes. Every caller must state which document it
 * is plotting, and `undefined` is the honest way to say "this one declares
 * none" — {@link fillModePaintsSolid} then applies the absent-means-1 rule, the
 * same one the canvas applies to `RenderableEntity.fillmode`.
 */
export function bandPathPrimitive(
  entityId: string,
  layer: string,
  band: WidePolylineBand,
  style: PlotStyle,
  fillmode: number | undefined,
): PathPrimitive {
  const solid = fillModePaintsSolid(fillmode);
  return {
    type: "path",
    entityId,
    layer,
    start: band.start,
    segments: band.segments,
    closed: true,
    // At 0 the rings are STROKED instead. `closed: true` above already makes
    // each ring emit its `h` before the paint op, and `batchPaint.ts` walks
    // `subpaths` for a stroke exactly as it does for a fill, so the annulus's
    // inner edge is stroked too — one `S` over every ring.
    fill: solid ? "nonzero" : "none",
    ...(solid ? { stroke: false } : {}),
    ...(band.subpaths ? { subpaths: band.subpaths } : {}),
    style,
  };
}

/**
 * The top-level "this entity plots as its filled band" tail — the band twin of
 * `strokedPathPrimitive.ts`'s {@link strokedPathPrimitives}, carrying the one
 * copy of the same two rules: honour the CTB/STB do-not-plot sentinel
 * (PDF-6.3b — `style.skip` ⇒ emit NOTHING) and fall the OCG layer back to "0".
 * `insertConverter.ts` does not use it: a block child resolves its style
 * through the INSERT's BYBLOCK chain (`childStyle`) and already holds the
 * resolved layer, so it calls {@link bandPathPrimitive} directly.
 */
export function bandPrimitives(
  entity: StyledEntity & { id: string },
  ctx: CadConvertContext,
  band: WidePolylineBand,
): CadPrimitive[] {
  const style = resolvePlotStyle(entity, ctx);
  if (style.skip) return [];
  return [
    bandPathPrimitive(entity.id, entity.layer ?? "0", band, style, ctx.fillmode),
  ];
}
